import { Link } from 'react-router-dom';
import { track } from './analytics';
import { microsoftStore } from '../data/site';

export function PageHero({
  eyebrow = 'Transport Book',
  title,
  description,
  children,
  compact = false,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  children?: React.ReactNode;
  /** A smaller heading, for a page people come to read rather than to be sold to. */
  compact?: boolean;
}) {
  return (
    <section className={compact ? 'page-hero page-hero--compact' : 'page-hero'}>
      <div className="container">
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p className="lead">{description}</p>
        {children}
      </div>
    </section>
  );
}

/** The Microsoft Store's four-square mark, drawn rather than fetched so the
 * badge inherits the page's colours and needs no external asset. */
function StoreMark() {
  return (
    <svg className="store-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 2h9.4v9.4H2zM12.6 2H22v9.4h-9.4zM2 12.6h9.4V22H2zM12.6 12.6H22V22h-9.4z" />
    </svg>
  );
}

/** Install button for the Windows Store listing. `variant="badge"` renders the
 * two-line store badge; the default renders an ordinary primary button. */
export function MicrosoftStoreButton({
  variant = 'button',
  className = '',
}: {
  variant?: 'button' | 'badge';
  className?: string;
}) {
  const trackStore = () =>
    track('download_click', {
      channel: 'store',
      platform: 'windows',
      destination: microsoftStore.url,
    });
  if (variant === 'badge')
    return (
      <a
        className={`store-badge ${className}`.trim()}
        href={microsoftStore.url}
        rel="noopener"
        onClick={trackStore}
      >
        <StoreMark />
        <span>
          <small>Download from</small>
          <strong>Microsoft Store</strong>
        </span>
      </a>
    );
  return (
    <a
      className={`button primary ${className}`.trim()}
      href={microsoftStore.url}
      rel="noopener"
      onClick={trackStore}
    >
      <StoreMark />
      {microsoftStore.label}
    </a>
  );
}

/** A product screenshot in a window frame. Sources in public/product are 16:9
 * renders of the desktop captures, generated by `pnpm product-shots:sync`.
 *
 * The desktop app is captured in both of its colour schemes, and the shot
 * follows the site theme. Both pictures are in the markup (the prerendered page
 * cannot know the theme) and CSS hides one. The dark picture is always lazy, so
 * a hidden one is never downloaded; `priority` applies to the light picture. */
export function ProductShot({
  slug,
  alt,
  priority,
  className = '',
}: {
  slug: string;
  alt: string;
  priority?: boolean;
  className?: string;
}) {
  return (
    <figure className={`shot ${className}`.trim()}>
      <div className="shot-bar" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <ShotPicture file={slug} alt={alt} priority={priority} className="shot-light" />
      <ShotPicture file={`${slug}-dark`} alt={alt} className="shot-dark" />
    </figure>
  );
}

function ShotPicture({
  file,
  alt,
  priority,
  className,
}: {
  file: string;
  alt: string;
  priority?: boolean;
  className: string;
}) {
  return (
    <picture className={className}>
      <source
        type="image/webp"
        srcSet={`/product/${file}-960.webp 960w, /product/${file}-1600.webp 1600w`}
        sizes="(max-width: 900px) 100vw, 1100px"
      />
      <img
        src={`/product/${file}-1600.jpg`}
        width="1600"
        height="900"
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : undefined}
      />
    </picture>
  );
}

/** The 3D renders in public/3d: the dark-and-gold "premium" set of 3dicons
 * (3dicons.co, CC0 — free for commercial use, no attribution required),
 * converted to 320px WebP with transparency so they sit on light and dark
 * backgrounds alike. Always decorative: the heading beside one says what it is. */
export type Icon3DName =
  | 'calendar'
  | 'card'
  | 'chart'
  | 'computer'
  | 'file-text'
  | 'folder'
  | 'key'
  | 'link'
  | 'map-pin'
  | 'rupee'
  | 'shield'
  | 'tool';

export function Icon3D({
  name,
  size = 72,
  className = '',
}: {
  name: Icon3DName;
  size?: number;
  className?: string;
}) {
  return (
    <img
      className={`icon-3d ${className}`.trim()}
      src={`/3d/${name}.webp`}
      width={size}
      height={size}
      alt=""
      loading="lazy"
      decoding="async"
    />
  );
}

export function CTA({
  actions,
  title = 'See how Transport Book fits your operation',
  body = 'Tell us how your logistics team works today. We will walk through the relevant desktop and mobile workflows.',
}: {
  /** In place of the usual ways in, where someone is past them. */
  actions?: React.ReactNode;
  title?: string;
  body?: string;
}) {
  return (
    <section className="section compact">
      <div className="container">
        <div className="cta">
          <div className="cta-objects" aria-hidden="true">
            <Icon3D name="rupee" size={150} className="cta-object cta-object--a" />
            <Icon3D name="calendar" size={120} className="cta-object cta-object--b" />
            <Icon3D name="chart" size={130} className="cta-object cta-object--c" />
            <Icon3D name="map-pin" size={110} className="cta-object cta-object--d" />
          </div>
          <div className="cta-copy">
            <h2>{title}</h2>
            <p>{body}</p>
          </div>
          <div className="cta-actions">
            {actions ?? (
              <>
                <Link
                  className="button primary"
                  to="/start-trial"
                  onClick={() => track('trial_cta_click', { placement: 'cta' })}
                >
                  Start free trial
                </Link>
                <Link
                  className="button on-dark"
                  to="/request-demo"
                  onClick={() => track('demo_cta_click')}
                >
                  Request a demo
                </Link>
                <MicrosoftStoreButton variant="badge" className="on-dark" />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LegalPage({
  title,
  description,
  updated = '20 July 2026',
  children,
}: {
  title: string;
  description: string;
  updated?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <PageHero eyebrow="Legal" title={title} description={description} compact />
      <section className="section">
        <div className="container prose legal-prose">
          <p>
            <strong>Last updated:</strong> {updated}
          </p>
          {children}
        </div>
      </section>
    </>
  );
}
